import React, { useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import Select, { components as selectComponents } from "react-select";
import { FileText, Mail, Users, X } from "lucide-react";
import Loader from "../components/loader";
import { formatCoupleOptionLabel } from "../utils/coupleLabels";
import "../assets/css/rosterActionModals.css";

const CheckboxOption = (props) => (
  <selectComponents.Option {...props}>
    <span className="ram-option">
      <input type="checkbox" checked={props.isSelected} readOnly />
      <span>{props.label}</span>
    </span>
  </selectComponents.Option>
);

const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

function SendFileModal({
  show,
  handleClose,
  onSend,
  loader,
  retreatRoastData = [],
  fileType = "directory",
}) {
  const [selectedRetreatants, setSelectedRetreatants] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState([]);
  const [error, setError] = useState("");
  const isProgram = fileType === "program";

  const retreatantOptions = useMemo(() => {
    return retreatRoastData
      .filter((item) => item.attendeeType === "Retreatant")
      .map((item) => ({
        value: item.id,
        label: formatCoupleOptionLabel(item),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [retreatRoastData]);

  const teamOptions = useMemo(() => {
    return retreatRoastData
      .filter((item) => item.attendeeType === "Team")
      .map((item) => ({
        value: item.id,
        label: formatCoupleOptionLabel(item),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [retreatRoastData]);

  const isAllRetreatantsSelected =
    retreatantOptions.length > 0 &&
    selectedRetreatants.length === retreatantOptions.length;

  const isAllTeamSelected =
    teamOptions.length > 0 && selectedTeam.length === teamOptions.length;

  const selectedCount = selectedRetreatants.length + selectedTeam.length;

  const toggleSelectAllRetreatants = () => {
    setError("");
    if (isAllRetreatantsSelected) {
      setSelectedRetreatants([]);
    } else {
      setSelectedRetreatants(retreatantOptions);
    }
  };

  const toggleSelectAllTeam = () => {
    setError("");
    if (isAllTeamSelected) {
      setSelectedTeam([]);
    } else {
      setSelectedTeam(teamOptions);
    }
  };

  const resetState = () => {
    setSelectedRetreatants([]);
    setSelectedTeam([]);
    setError("");
  };

  const handleModalClose = () => {
    handleClose();
    resetState();
  };

  const handleConfirm = () => {
    if (selectedRetreatants.length === 0 && selectedTeam.length === 0) {
      setError("Select at least one retreatant or team couple.");
      return;
    }

    const recipientTypes = [];
    const recipientIds = [];

    if (selectedRetreatants.length > 0) {
      recipientTypes.push("Retreatant");
      recipientIds.push(...selectedRetreatants.map((r) => r.value));
    }

    if (selectedTeam.length > 0) {
      recipientTypes.push("Team");
      recipientIds.push(...selectedTeam.map((t) => t.value));
    }

    onSend({
      recipientTypes,
      recipientIds,
    });

    resetState();
  };

  return (
    <Modal
      show={show}
      onHide={handleModalClose}
      centered
      className="ram-modal"
      dialogClassName="ram-dialog ram-dialog-wide"
      contentClassName="ram-content"
    >
      <div className="ram-shell">
        <header className="ram-header">
          <div className="ram-mark" aria-hidden="true">
            {isProgram ? <FileText size={20} /> : <Mail size={20} />}
          </div>
          <div className="ram-header-copy">
            <p className="ram-kicker">Share with couples</p>
            <h2>{isProgram ? "Send Program" : "Send Directory"}</h2>
            <p>
              Choose the Retreatants and Team couples who should receive this{" "}
              {isProgram ? "Program" : "Directory"}.
            </p>
          </div>
          <button
            type="button"
            className="ram-close"
            onClick={handleModalClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </header>

        {loader ? (
          <div className="ram-loading">
            <Loader />
          </div>
        ) : (
          <div className="ram-body">
            <div className="ram-split">
              <section className="ram-column">
                <div className="ram-column-head">
                  <h3>Retreatants</h3>
                  <span className="ram-pill">
                    {selectedRetreatants.length}/{retreatantOptions.length}
                  </span>
                </div>
                <label
                  className={`ram-check${
                    isAllRetreatantsSelected ? " is-on" : ""
                  }${retreatantOptions.length === 0 ? " is-disabled" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={isAllRetreatantsSelected}
                    onChange={toggleSelectAllRetreatants}
                    disabled={retreatantOptions.length === 0}
                  />
                  Select all Retreatants
                </label>
                <Select
                  isMulti
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  options={retreatantOptions}
                  components={{ Option: CheckboxOption }}
                  value={selectedRetreatants}
                  onChange={(vals) => {
                    setError("");
                    setSelectedRetreatants(vals || []);
                  }}
                  placeholder="Select Retreatants"
                  classNamePrefix="ram-select"
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
                {retreatantOptions.length === 0 ? (
                  <p className="ram-empty-inline">
                    No Retreatants are on this Roster.
                  </p>
                ) : null}
              </section>

              <section className="ram-column">
                <div className="ram-column-head">
                  <h3>Team</h3>
                  <span className="ram-pill">
                    {selectedTeam.length}/{teamOptions.length}
                  </span>
                </div>
                <label
                  className={`ram-check${isAllTeamSelected ? " is-on" : ""}${
                    teamOptions.length === 0 ? " is-disabled" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isAllTeamSelected}
                    onChange={toggleSelectAllTeam}
                    disabled={teamOptions.length === 0}
                  />
                  Select all Team
                </label>
                <Select
                  isMulti
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  options={teamOptions}
                  components={{ Option: CheckboxOption }}
                  value={selectedTeam}
                  onChange={(vals) => {
                    setError("");
                    setSelectedTeam(vals || []);
                  }}
                  placeholder="Select Team"
                  classNamePrefix="ram-select"
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
                {teamOptions.length === 0 ? (
                  <p className="ram-empty-inline">
                    No team couples are on this roster.
                  </p>
                ) : null}
              </section>
            </div>

            <div className="ram-selection">
              <span>
                <Users size={14} />
                {selectedCount} selected
              </span>
            </div>
            <p className="ram-note ram-note-follow">
              Pick names from each list, or select everyone in a group.
            </p>
            {error ? <p className="ram-error">{error}</p> : null}
          </div>
        )}

        <footer className="ram-footer">
          <button
            type="button"
            className="ram-btn ram-btn-quiet"
            onClick={handleModalClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className={`ram-btn ram-btn-primary${loader ? " is-busy" : ""}`}
            onClick={handleConfirm}
            disabled={loader || selectedCount === 0}
          >
            {loader
              ? "Sending..."
              : selectedCount > 0
                ? `Send to ${selectedCount}`
                : "Send"}
          </button>
        </footer>
      </div>
    </Modal>
  );
}

export default SendFileModal;
